import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { useCmsContent } from '@/hooks/useCmsContent';
import PageSEO from '@/components/PageSEO';
import { 
  GraduationCap, 
  Briefcase, 
  Award, 
  Users,
  CheckCircle,
  ArrowRight,
  Globe,
  BookOpen,
  ExternalLink,
  Newspaper
} from 'lucide-react';

const ChiSonoPage = () => {
  const { t: cms } = useCmsContent('chi-sono');
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const tx = (cmsKey, i18nKey) => lang === 'it' ? (cms(cmsKey) || t(i18nKey)) : t(i18nKey);
  const competenze = [
    'Consulenza fiscale per startup e PMI',
    'E-commerce e attività digitali',
    'Fiscalità internazionale',
    'Regimi fiscali agevolati',
    'Fatturazione elettronica',
    'Revisione contabile',
    'Business planning',
    'Dichiarazioni fiscali'
  ];

  const timeline = [
    {
      year: 'Formazione',
      title: 'Laurea in Economia',
      description: 'Laurea in Economia e Commercio con specializzazione in discipline tributarie.',
      icon: GraduationCap
    },
    {
      year: 'Abilitazione',
      title: 'Dottore Commercialista',
      description: 'Iscrizione all\'Albo dei Dottori Commercialisti e degli Esperti Contabili.',
      icon: Award
    },
    {
      year: 'Esperienza',
      title: 'Studio Professionale',
      description: 'Apertura dello studio con focus su startup, e-commerce e nuovi business digitali.',
      icon: Briefcase
    },
    {
      year: 'Oggi',
      title: 'Consulenza Online',
      description: 'Servizi di consulenza fiscale online per clienti in tutta Italia e all\'estero.',
      icon: Globe
    }
  ];

  return (
    <div className="min-h-screen" data-testid="chi-sono-page">
      <PageSEO path="/chi-sono" />
      {/* Hero Section */}
      <section className="bg-white py-20 lg:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <span className="text-sm font-medium tracking-widest uppercase text-indigo-600 mb-4 block">
                Chi Sono
              </span>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 mb-6 tracking-tight">
                {t('page_title', 'Dott. Aurelio\nAzzano Cantarutti').split('\n').map((line, i) => (
                  <span key={i}>
                    {i === 1 ? <span className="text-indigo-600">{line}</span> : line}
                    {i === 0 && <br />}
                  </span>
                ))}
              </h1>
              <p className="text-lg text-slate-600 leading-relaxed mb-6">
                {t('bio_1', "Dottore Commercialista e Revisore Contabile specializzato nell'assistenza alle startup e ai nuovi business legati al web. Offro consulenza completa e integrata per l'avvio e la gestione di attività di e-commerce e attività online.")}
              </p>
              <p className="text-lg text-slate-600 leading-relaxed mb-8">
                {t('bio_2', "La mia offerta è caratterizzata da un elevato standard qualitativo, continuo aggiornamento scientifico, interattività con il cliente e pricing adeguato alla realtà dimensionale dell'azienda.")}
              </p>
              <Link to="/consulenza">
                <Button size="lg" className="btn-accent" data-testid="book-consultation-btn">
                  Prenota una consulenza
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="relative"
            >
              <img
                src="https://customer-assets.emergentagent.com/job_810c1cbb-bb83-4596-a92c-cec6eb0dba96/artifacts/xa8e3nzm_%7BA4AEA714-6847-452C-BCD4-2E38AD625D7C%7D.png"
                alt="Dott. Aurelio Azzano Cantarutti"
                className="rounded-2xl shadow-2xl"
              />
              <div className="absolute -bottom-6 -right-6 bg-white rounded-xl shadow-xl p-6 border border-slate-100">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-indigo-100 rounded-full flex items-center justify-center">
                    <Users className="w-6 h-6 text-indigo-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900">+500</p>
                    <p className="text-sm text-slate-500">Clienti assistiti</p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Competenze */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-sm font-medium tracking-widest uppercase text-indigo-600 mb-4 block">
              Competenze
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
              Aree di specializzazione
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {competenze.map((competenza, index) => (
              <motion.div
                key={competenza}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.05 * index }}
                className="flex items-center gap-3 bg-white p-4 rounded-lg border border-slate-200"
              >
                <CheckCircle className="w-5 h-5 text-indigo-600 flex-shrink-0" />
                <span className="text-slate-700 font-medium">{competenza}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-20 lg:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-sm font-medium tracking-widest uppercase text-indigo-600 mb-4 block">
              Percorso Professionale
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900">
              La mia storia
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {timeline.map((item, index) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 * index }}
                className="relative"
              >
                <div className="bg-slate-50 rounded-xl p-6 h-full border border-slate-200 hover:border-indigo-200 hover:shadow-lg transition-all">
                  <div className="w-12 h-12 bg-indigo-100 rounded-xl flex items-center justify-center mb-4">
                    <item.icon className="w-6 h-6 text-indigo-600" />
                  </div>
                  <span className="text-sm font-medium text-indigo-600 mb-2 block">
                    {item.year}
                  </span>
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">
                    {item.title}
                  </h3>
                  <p className="text-slate-600 text-sm">
                    {item.description}
                  </p>
                </div>
                {index < timeline.length - 1 && (
                  <div className="hidden lg:block absolute top-1/2 -right-4 w-8 h-0.5 bg-slate-200" />
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Philosophy */}
      <section className="py-20 bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="w-14 h-14 bg-indigo-600 rounded-xl flex items-center justify-center mb-6">
                <BookOpen className="w-7 h-7 text-white" />
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
                {t('philosophy_title', 'La mia filosofia professionale')}
              </h2>
              <p className="text-lg text-slate-300 leading-relaxed mb-6">
                {t('philosophy_1', "Credo in un approccio alla consulenza fiscale che metta al centro il cliente e le sue esigenze specifiche. Ogni business è unico e merita soluzioni personalizzate e innovative.")}
              </p>
              <p className="text-lg text-slate-300 leading-relaxed">
                {t('philosophy_2', "Il mio impegno è fornire un servizio di alta qualità, mantenendo un rapporto diretto e trasparente con ogni cliente, indipendentemente dalle dimensioni della sua attività.")}
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="grid grid-cols-2 gap-6"
            >
              {[
                { value: '+30', label: 'Anni di esperienza' },
                { value: '+500', label: 'Clienti soddisfatti' },
                { value: '100%', label: 'Consulenze online' },
                { value: '24h', label: 'Risposta garantita' }
              ].map((stat, index) => (
                <div
                  key={stat.label}
                  className="bg-slate-800 rounded-xl p-6 text-center"
                >
                  <p className="text-4xl font-bold text-indigo-400 mb-2">{stat.value}</p>
                  <p className="text-slate-400 text-sm">{stat.label}</p>
                </div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Rassegna Stampa */}
      <section className="py-20 bg-slate-50" data-testid="press-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <span className="text-sm font-medium tracking-widest uppercase text-indigo-600 mb-4 block">
              {t('chiSono.pressTitle')}
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900">
              {t('chiSono.pressSubtitle')}
            </h2>
          </motion.div>

          <motion.a
            href="https://legalcommunity.it/lexant-e-azzano-con-mbm-holding-nella-cessione-di-morandi-bortot-al-gruppo-saviola/"
            target="_blank"
            rel="noopener noreferrer"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="block max-w-3xl mx-auto bg-white rounded-2xl border border-slate-200 p-8 hover:border-indigo-300 hover:shadow-lg transition-all group"
            data-testid="press-article-link"
          >
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 bg-indigo-50 rounded-lg flex items-center justify-center">
                <Newspaper className="w-5 h-5 text-indigo-600" />
              </div>
              <div>
                <p className="font-semibold text-slate-900">{t('chiSono.pressSource')}</p>
                <p className="text-xs text-slate-500">{t('chiSono.pressSourceType')}</p>
              </div>
              <ExternalLink className="w-4 h-4 text-slate-400 group-hover:text-indigo-600 transition-colors ml-auto" />
            </div>
            <h3 className="text-xl font-bold text-slate-900 mb-4 group-hover:text-indigo-600 transition-colors">
              {t('chiSono.pressArticleTitle')}
            </h3>
            <p className="text-slate-600 leading-relaxed mb-4">
              {t('chiSono.pressArticleDesc')}
            </p>
            <p className="text-sm text-indigo-600 font-medium group-hover:underline flex items-center gap-1">
              {t('chiSono.pressReadMore')}
              <ArrowRight className="w-4 h-4" />
            </p>
          </motion.a>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Pronto a iniziare?
            </h2>
            <p className="text-lg text-slate-600 mb-8">
              Prenota una consulenza online per discutere delle tue esigenze 
              e trovare insieme le soluzioni migliori per il tuo business.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/consulenza">
                <Button size="lg" className="btn-accent w-full sm:w-auto">
                  Prenota Consulenza
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
              <Link to="/contatti">
                <Button size="lg" variant="outline" className="w-full sm:w-auto">
                  Contattami
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default ChiSonoPage;
